import { Component, OnDestroy, OnInit } from '@angular/core';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { ProductDao } from '@app/dao/product/product.dao';
import { InfoOption } from '@app/forms/subscription/definition/interface/pledge-options';
import { SubscriptionFacadeService } from '@app/forms/subscription/services/subscription-facade.service';
import { Subject, take } from 'rxjs';
import { setupConditionalValidator, toNumber } from '../../subscription-form.utils';
import { EaSubscriptionBuilder } from './ea-subscription-form.builder';

const ACQUISITION_RESIDENCE_CHOICES = [
  { id: 'SEUL', label: 'Seul' },
  { id: 'A_PLUSIEURS', label: 'A plusieurs' },
];

const JUSTIFICATION_SOUSCRIPTION_OPTIONS = [
  { value: 'ECARTS_COUVERTURE', label: 'Oui, écarts de couverture identifiés' },
  { value: 'GARANTIES_PLUS_FAVORABLES', label: 'Oui, définition des garanties plus favorable' },
  { value: 'CHANGEMENT_SITUATION', label: 'Oui, changement de situation personnelle / professionnelle' },
  { value: 'NON_RECOMMANDEE', label: 'Non — souscription non recommandée' },
];

@Component({
    selector: 'contigency-regime-profession',
    templateUrl: './contigency-regime-profession.component.html',
    styleUrls: ['./contigency-regime-profession.component.scss'],
    standalone: false
})
export class ContigencyRegimeProfessionComponent
  extends EaSubscriptionBuilder
  implements OnInit, OnDestroy
{
  parameters;
  regimeOptions: { id: string; label: string }[] = [];
  acquisitionResidenceOptions = ACQUISITION_RESIDENCE_CHOICES;
  justificationOptions = JUSTIFICATION_SOUSCRIPTION_OPTIONS;
  optionWarning: InfoOption = {
    icon: 'warning',
    backgroundColor: '#fae4e4',
    iconColor: '#a00d0d',
    textColor: '#a00d0d',
  };
  private destroy$ = new Subject<void>();

  constructor(
    protected formConstructor: PrFormConstructor,
    private subscriptionFacade: SubscriptionFacadeService,
    protected productDao: ProductDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    const pledge = this.subscriptionFacade.getPledge();
    this.parameters = pledge?.parameters;
    this.subscriptionFacade.initForm(this, { ...this.parameters });

    this.productDao.getRegimeObligatoireRef().pipe(take(1)).subscribe(data => {
      this.regimeOptions = data;
    });

    const params = this.products?.get('0')?.get('parameters');
    const getCtrl = (name: string) => params?.get(name);

    setupConditionalValidator(
      getCtrl('proprietaireResidencePrincipale'),
      [getCtrl('acquisitionResidencePrincipale'), getCtrl('quotiteAssuranceEmprunt')],
      value => !!value,
      this.destroy$,
    );

    setupConditionalValidator(
      getCtrl('tnsChargesProfessionnelles'),
      [getCtrl('montantChargesProfessionnelles')],
      value => !!value,
      this.destroy$,
    );

    setupConditionalValidator(
      getCtrl('preservationSalaireArretTravail'),
      [getCtrl('montantArretTravail')],
      value => !!value,
      this.destroy$,
    );

    setupConditionalValidator(
      getCtrl('preservationSalaireInvalidite'),
      [getCtrl('montantInvalidite')],
      value => !!value,
      this.destroy$,
    );
  }

  onSubmit() {
    const pledge = this.subscriptionFacade.getPledge();
    const params = this.products?.get('0')?.get('parameters')?.value;
    if (pledge && params) {
      const ownerId = pledge.owners?.[0]?.ownerId || pledge.owners?.[0]?.id;
      if (ownerId) {
        const body = {
          ownerId,
          accountId: Number(pledge.accountId),
          regimeObligatoire: params.regimeObligatoire || undefined,
          beneficeNetAnnuel: toNumber(params.beneficeNetAnnuel),
          proprietaireResidencePrincipale: params.proprietaireResidencePrincipale,
          acquisitionResidencePrincipale: params.acquisitionResidencePrincipale || undefined,
          quotiteAssuranceEmprunt: toNumber(params.quotiteAssuranceEmprunt),
          renteConjoint: params.renteConjoint,
          renteEducationEnfants: params.renteEducationEnfants,
          fumeur: params.fumeur,
          optionDosPsy: params.optionDosPsy,
          preservationSalaireArretTravail: params.preservationSalaireArretTravail,
          montantArretTravail: toNumber(params.montantArretTravail),
          preservationSalaireInvalidite: params.preservationSalaireInvalidite,
          montantInvalidite: toNumber(params.montantInvalidite),
          tnsChargesProfessionnelles: params.tnsChargesProfessionnelles,
          montantChargesProfessionnelles: toNumber(params.montantChargesProfessionnelles),
          avocatCollaborateur: params.avocatCollaborateur,
          dossiersPersonnels: params.dossiersPersonnels,
          notes: params.notes || undefined,
          risqueDoubleIndemnisation: params.risqueDoubleIndemnisation,
          clientInformeRisqueCumul: params.clientInformeRisqueCumul,
          justificationSouscription: Array.isArray(params.justificationSouscription) ? params.justificationSouscription : [],
          justificationCommentaire: params.justificationCommentaire || undefined,
        };
        this.productDao.savePrevoyanceHypotheses(Number(pledge.accountId), body)
          .pipe(take(1))
          .subscribe({ error: () => this.notificationService.error('La sauvegarde des informations prévoyance a échoué') });
      }
    }
    this.subscriptionFacade.submitPledge(this);
  }

  prev() {
    this.subscriptionFacade.previousStep();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
